import { ECurrency, symbolCurrency } from '@lbs/currency'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { FC } from 'react'
import { moneyFormat } from '../../utils/money'

interface FinanceSummaryProps {
  totalRecords: number
  loadedRecords: number
  totalAmount: number
  currency: ECurrency
  dateRange?: {
    from?: string
    to?: string
  }
}

/**
 * Компонент для отображения сводной информации по финансовым операциям
 * Показывает ключевые метрики для быстрого анализа
 */
export const FinanceSummary: FC<FinanceSummaryProps> = ({
  totalRecords,
  loadedRecords,
  totalAmount,
  currency,
  dateRange,
}) => {
  const isPositive = totalAmount > 0
  const isZero = totalAmount === 0

  let amountColor = 'text.primary'
  if (!isZero) {
    amountColor = isPositive ? 'success.main' : 'error.main'
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        p: 1.5,
        backgroundColor: 'background.default',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
      }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Итого за период:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace', color: amountColor }}>
          {isPositive ? '+' : ''}
          {moneyFormat(String(totalAmount.toFixed(2)))} {symbolCurrency[currency]}
        </Typography>
      </Box>

      <Box sx={{ width: '1px', height: 40, backgroundColor: 'divider' }} />

      {dateRange?.from && dateRange?.to && (
        <>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Период:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {dateRange.from} — {dateRange.to}
            </Typography>
          </Box>
          <Box sx={{ width: '1px', height: 40, backgroundColor: 'divider' }} />
        </>
      )}

      {/* Количество операций */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Всего операций:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
          {totalRecords.toLocaleString('ru-RU')}
        </Typography>
      </Box>

      <Box sx={{ width: '1px', height: 40, backgroundColor: 'divider' }} />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Загружено:
        </Typography>
        <Chip
          label={`${loadedRecords} из ${totalRecords}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
      </Box>
    </Stack>
  )
}
